import safePerformanceMarkStart from 'mini-performance/lib/utils/safePerformanceMarkStart';
import TimeConstants from 'mini-datetime-utils/lib/dateValues/TimeConstants';
import { emitDiagnostics } from 'mini-diagnostics/lib/events/diagnosticsEventsManager';
import { errorThatWillCauseAlert } from 'owa-trace';
import { getAndClearAccountSwitchParams } from './utils/getAndClearAccountSwitchParams';
import { getBootstrapPerformanceDiagnostics } from 'mini-boot-diagnostics/lib/bootstrapPerformanceDiagnostics';
import { getCriticalTimingData } from './getCriticalTimingData';
import { getCustomPayloadForAppRenderBootAnalysis } from './getCustomPayloadForAppRenderBootAnalysis';
import { getIsDarkTheme } from 'mini-theme';
import { getOwaWorkload } from 'owa-workloads';
import { logPerfMeasure } from './logPerfMeasure';
import { onSignal } from 'listeners/lib/signalTrackerListener';
import { safePerformanceMarkEnd } from 'mini-performance';
import {
    setItemInSessionStorage,
    getItemFromSessionStorage,
} from 'mini-session-storage/lib/utils/sessionStorageUtils';

/**
 * Indicates how the module was loaded.
 * 0 -> Full page reload via mini-owa launch or page refresh.
 * 1 -> First time in-app module switch (may involve downloading boot-bundle and critical data).
 * 2 -> Instant module switch (assets and data cached).
 */
export type ModuleLaunchType = 0 | 1 | 2;

/**
 * Log the Largest Surface Render metric (time from navigation start to LCP event)
 */
export async function trackLargestSurfaceRender(
    signalsToTrack: string[],
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    customPayloadToLog?: any,
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    getCustomDataAccumulated?: () => any,
    isModuleSwitch?: boolean,
    moduleLaunchType: ModuleLaunchType = 0
) {
    try {
        // If the document visibility state comes hidden here, assume that the page was hidden from the start.
        // This is the best assumption which can be made till the page visibility API improves to find past visibility changes with their timing
        const timeOfFirstPageHide =
            document.visibilityState === 'hidden' ? 0 : TimeConstants.MillisecondsInOneDay * 30;

        const accountSwitchParams = getAndClearAccountSwitchParams();

        const listOfSignalPromises = signalsToTrack.map(onSignal);

        const data = await Promise.race(listOfSignalPromises);
        safePerformanceMarkStart('LargestSurfaceRender');
        safePerformanceMarkEnd('LargestSurfaceRender');

        const payload = await getCustomPayloadForAppRenderBootAnalysis(customPayloadToLog);
        const timingData = getCriticalTimingData();

        const accumulatedData = getCustomDataAccumulated?.();

        const moduleLoadData = getItemFromSessionStorage('moduleLoad') || [];
        if (moduleLoadData.length < 5) {
            moduleLoadData.push(getOwaWorkload());
        }
        setItemInSessionStorage('moduleLoad', moduleLoadData);

        /* eslint-disable-next-line no-unsafe-optional-chaining --
         * undefined
         *	> Unsafe usage of optional chaining. If it short-circuits with 'undefined' the evaluation will throw TypeError. */
        const { beforePaintTime, afterPaintTime } = data?.customData;
        const flooredAfterPaintTime = Math.floor(+afterPaintTime);

        // Need to log it for some time to identify reasons for higher render end.
        const perfTime = {
            cdiff:
                window.performance?.timing?.fetchStart -
                window.performance?.timing?.navigationStart,
            beforePaint: Math.floor(+beforePaintTime),
        };

        const actSwtData = accountSwitchParams
            ? {
                  actSwt: JSON.stringify({
                      src: accountSwitchParams.srcSessionId,
                      dur: Date.now() - accountSwitchParams.startTs,
                  }),
              }
            : {};

        const startTime = flooredAfterPaintTime;
        const customData = {
            ...payload,
            ...timingData,
            ...perfTime,
            ...accumulatedData,
            ...actSwtData,
            ...getBootstrapPerformanceDiagnostics(),
            moduleLoad: JSON.stringify(moduleLoadData),
            wph: flooredAfterPaintTime > timeOfFirstPageHide,
            iph: false,
            mlt: moduleLaunchType,
            isDarkMode: getIsDarkTheme(),
        };

        const duration = 0;
        logPerfMeasure(
            {
                duration,
                startTime,
            } as PerformanceEntry,
            'LargestSurfaceRender',
            false /** logOnce */,
            true /** addToPageTimings */,
            true /** markAsCore */,
            customData
        );

        emitDiagnostics({
            name: 'Performance.LSR',
            data: {
                startTime,
                duration,
                endTime: startTime + duration,
                ...customData,
            },
        });
    } catch (error) {
        errorThatWillCauseAlert('[trackLargestSurfaceRender] Error: ' + error.message);
    }
}
